import React, { useState, useEffect } from 'react';
import { cemeteryAPI, backupAPI, importAPI, wipeAPI } from '../services/api';
import CemeteryCard from '../components/CemeteryCard';
import Toast from '../components/Toast';
import './DashboardPage.css';

function DashboardPage() {
  const [cemeteries, setCemeteries] = useState([]);
  const [backups, setBackups] = useState([]);
  const [imports, setImports] = useState([]);
  const [wipes, setWipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    loadDashboardData(controller.signal);
    return () => controller.abort();
  }, []);

  const loadDashboardData = async (signal) => {
    setLoading(true);
    setError(null);
    try {
      // Fetch cemeteries, backups, and imports in parallel
      const [cemResponse, backupResponse, importResponse, wipeResponse] = await Promise.all([
        cemeteryAPI.list({ signal }),
        backupAPI.getHistory({ page_size: 1000 }, { signal }),
        importAPI.getHistory({ page_size: 1000 }, { signal }),
        wipeAPI.getHistory({ page_size: 1000 }, { signal })
      ]);

      setCemeteries(cemResponse.data.cemeteries || cemResponse.data);
      setBackups(backupResponse.data.backups || []);
      setImports(importResponse.data.imports || []);
      setWipes(wipeResponse.data.wipes || []);
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      setError({ type: 'error', text: 'Failed to load dashboard data' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Helper to find the latest backup for a cemetery
  const getLastBackupInfo = (cemeteryUniqueName) => {
    const cemeteryBackups = backups.filter(b =>
      (b.cemetery_unique_name && b.cemetery_unique_name === cemeteryUniqueName) ||
      (b.zip_filename && b.zip_filename.includes(cemeteryUniqueName))
    );

    if (cemeteryBackups.length === 0) return null;

    // Sort by date descending
    cemeteryBackups.sort((a, b) => {
      const dateA = a.created_at || a.date || '';
      const dateB = b.created_at || b.date || '';
      return dateB.localeCompare(dateA);
    });

    return cemeteryBackups[0];
  };

  const getLastImportInfo = (cemeteryUniqueName) => {
    const cemeteryImports = imports.filter(i =>
      i.cemetery_unique_name === cemeteryUniqueName
    );

    if (cemeteryImports.length === 0) return null;

    cemeteryImports.sort((a, b) => {
      const dateA = a.created_at || '';
      const dateB = b.created_at || '';
      return dateB.localeCompare(dateA);
    });

    return cemeteryImports[0];
  };

  const getLastWipeInfo = (cemeteryUniqueName) => {
    const cemeteryWipes = wipes.filter(w =>
      w.cemetery_unique_name === cemeteryUniqueName
    );

    if (cemeteryWipes.length === 0) return null;

    cemeteryWipes.sort((a, b) => {
      const dateA = a.created_at || '';
      const dateB = b.created_at || '';
      return dateB.localeCompare(dateA);
    });

    return cemeteryWipes[0];
  };

  const filteredCemeteries = cemeteries.filter(cemetery =>
    cemetery.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cemetery.unique_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="dashboard-header">
        <div>
          <h1>Cemetery Portfolio</h1>
          <p>Overview of all managed cemeteries</p>
        </div>
        <div className="dashboard-actions">
          <input
            type="text"
            className="search-input"
            placeholder="Search cemeteries..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <Toast message={error} onDismiss={() => setError(null)} />

      {error && (
        <div className="no-results">
          <p className="text-muted">Failed to load dashboard data. Check your connection and try again.</p>
          <button className="btn btn-primary" onClick={() => loadDashboardData()}>
            Retry
          </button>
        </div>
      )}

      <div className="cemetery-grid">
        {filteredCemeteries.length === 0 ? (
          <div className="no-results">
            <p className="text-muted">No cemeteries found matching "{searchQuery}"</p>
          </div>
        ) : (
          filteredCemeteries.map((cemetery) => (
            <CemeteryCard
              key={cemetery.id || cemetery.unique_name}
              cemetery={cemetery}
              lastBackup={getLastBackupInfo(cemetery.unique_name)}
              lastImport={getLastImportInfo(cemetery.unique_name)}
              lastWipe={getLastWipeInfo(cemetery.unique_name)}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default DashboardPage;
